import type { CSSProperties, ReactNode } from 'react'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import { timestampMs } from '../../../utils/timezone.js'

const CHANNEL_LABELS: Record<string, string> = {
  chat: 'Web',
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
}

/** The subset of ConversationItem this list actually renders — kept
 *  narrow so both the conversation inbox's ConversationItem and the Live Chat
 *  sidebar's LiveChat (apps/web/src/hooks/useLiveChat.ts) can satisfy it
 *  without a full ticket-shaped adapter. */
export interface ChatListEntry {
  id: string
  customerName: string
  customerHandle: string
  customerEmail?: string | null
  customerPhone?: string | null
  clientId?: string | null
  channel: string
  timestamp: string
  createdAt: string
  preview: string
  isRead: boolean
  unreadCount?: number
  assignedAgentId?: string | null
  assignedAt?: string | null
  aiAssigned?: boolean | undefined
  aiAgentName?: string | null | undefined
  status?: string
  kind?: 'live_chat' | 'conversation'
  /** For Telegram: 'private', 'group', 'supergroup', 'channel' */
  chatType?: string | null
}

export interface ChatConversationListProps {
  conversations: ChatListEntry[]
  selectedId: string | null
  loading: boolean
  onSelect: (id: string) => void
  title?: string
  filterBar?: ReactNode
  className?: string
  style?: CSSProperties
}

function relativeTime(iso: string): string {
  const then = timestampMs(iso)
  if (isNaN(then)) return ''
  const diffMs = Date.now() - then
  const min = Math.floor(diffMs / 60_000)
  if (min < 1) return 'now'
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  return `${Math.floor(hr / 24)}d`
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase()
}

export default function ChatConversationList({
  conversations,
  selectedId,
  loading,
  onSelect,
  title = 'Chats',
  filterBar,
  className = '',
  style,
}: ChatConversationListProps): ReactNode {
  return (
    <div style={style} className={`flex h-full w-full shrink-0 flex-col overflow-hidden border-r border-slate-200 bg-white md:w-72 max-md:!w-full ${className}`}>
      <div className="shrink-0 border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      {filterBar && <div className="shrink-0 border-b border-slate-200 px-3 py-3">{filterBar}</div>}
      <div className="min-h-0 flex-1 overflow-y-auto thin-scrollbar" role="listbox" aria-label="Chat conversations">
        {loading && conversations.length === 0 && (
          <div className="space-y-2 p-3" data-testid="chat-list-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        )}
        {!loading && conversations.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 py-10 text-center text-slate-400">
            <p className="text-sm">No chat conversations yet.</p>
          </div>
        )}
        {conversations.map((c) => {
          const isSelected = c.id === selectedId
          const chConfig = getChannelConfig(c.channel)
          const ChIcon = chConfig.icon
          return (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => onSelect(c.id)}
              className={[
                'flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors',
                isSelected ? 'bg-blue-50' : 'hover:bg-slate-50',
              ].join(' ')}
            >
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-medium text-slate-600">
                {initials(c.customerName)}
                <div className={`absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full ${chConfig.bg} ring-2 ring-white`}>
                  <ChIcon className={`h-2.5 w-2.5 ${chConfig.color}`} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span
                      className={[
                        'truncate text-sm',
                        c.isRead ? 'font-normal text-slate-700' : 'font-semibold text-slate-900',
                      ].join(' ')}
                    >
                      {c.customerName}
                    </span>
                    <span
                      className={[
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                        chConfig.bg,
                        chConfig.color,
                      ].join(' ')}
                    >
                      {CHANNEL_LABELS[c.channel] ?? chConfig.label}
                    </span>
                  </div>
                  <span className="shrink-0 text-[10px] text-slate-400">
                    {relativeTime(c.timestamp)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-slate-500">{c.preview}</span>
                  {!c.isRead && c.unreadCount !== undefined && c.unreadCount > 0 ? (
                    <span className="ml-auto flex-shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                      {c.unreadCount}
                    </span>
                  ) : !c.isRead ? (
                    <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600" aria-label="unread" />
                  ) : null}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
